import { STANDARD_12H_DATE_TIME_FORMAT } from '@solinkcorp/constants';
import moment from 'moment-timezone';

export type DateLike = Date | number | string;

export function validateDateInFuture(date: Date | number): number {
  return Math.min(date.valueOf(), Date.now());
}

export function startOfDay(date: DateLike, timeZone: string): Date {
  return moment.tz(date, timeZone).startOf('day').toDate();
}

export function endOfDay(date: DateLike, timeZone: string): Date {
  return moment.tz(date, timeZone).endOf('day').toDate();
}

/**
 * Returns true if the given date is today.
 */
export function isToday(date: DateLike, timeZone: string): boolean {
  return moment.tz(date, timeZone).isSame(Date.now(), 'day');
}

export function isSameDay(
  d1: DateLike | undefined,
  d2: DateLike | undefined,
  timeZone: string,
): boolean {
  return d1 !== undefined && d2 !== undefined && moment.tz(d1, timeZone).isSame(d2, 'day');
}

/**
 * Add a unit of time to a date.
 */
export function add(
  date: DateLike,
  timeZone: string,
  count: number,
  unit: 'day' | 'week' | 'month' | 'year',
) {
  return moment.tz(date, timeZone).add(count, unit).toDate();
}

export function getLocal12HrDate(timestamp: DateLike, timezoneId: string): string {
  return moment.tz(timestamp, timezoneId).format(STANDARD_12H_DATE_TIME_FORMAT);
}

/**
 * Returns the current local timezone.
 */
export function getLocalTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Returns true if the two given time zones are the same offset on the given day.
 */
export function areTimezonesSameTime(timezone1: string, timezone2: string, date: Date): boolean {
  const time1 = moment.tz(date, timezone1);
  const time2 = moment.tz(date, timezone2);
  return time1.utcOffset() === time2.utcOffset();
}

export function isSameAsLocalTimeZone(timeZone: string | undefined, date: Date): boolean {
  return timeZone === undefined || areTimezonesSameTime(timeZone, getLocalTimeZone(), date);
}

export function getTimezoneAbbr(timezoneId: string | undefined) {
  return moment.tz(timezoneId ?? 'GMT').zoneAbbr();
}

/**
 * Gets the start of day 7 days ago
 */
export function getSevenDaysAgoStart(timezoneId: string): Date {
  return moment.tz(timezoneId).subtract(7, 'days').startOf('day').toDate();
}

/**
 * Gets the end of the specified date
 */
export function getEndOfDay(date: Date, timezoneId: string): Date {
  return moment.tz(date, timezoneId).endOf('day').toDate();
}

/**
 * Gets the start of the specified date
 */
export function getStartOfDay(date: Date, timezoneId: string): Date {
  return moment.tz(date, timezoneId).startOf('day').toDate();
}
